import React, { useState, useEffect } from "react";
import { UserAvatar, Icon } from "../../../components/Component";
import SimpleBar from "simplebar-react";
import { Button, Spinner, Modal, ModalBody, FormGroup, Label, Input, Badge } from "reactstrap";
import Swal from "sweetalert2";
import { SharedDoc, SharedImage, SharedVideo } from "./ChatPartials";
import { serviceGetContact } from "services/contact";
import { serviceGetMedia } from "services/media";
import moment from "moment";
import { serviceGetCampaign } from "services/campaign";
import { serviceGetTicketsByUserId } from "services/tickets";
import { serviceSubmitBlockRequest } from "services/contactBlock";
import { useContactBlockStatus } from "hooks/useContactBlockStatus";
import getTextColor from "components/custom/getTextColor";
import { utilNumberPhoneConvention } from "./utils/ChatUtils";

const BLOCK_REASON_OPTIONS = [
  { value: "INAPPROPRIATE_CONTENT", label: "Kirim konten tidak senonoh" },
  { value: "PROFIT_CENTER_ISSUE", label: "Masalah profit center" },
  { value: "SPAM", label: "Spam" },
  { value: "OTHER", label: "Lainnya" },
];

const ChatSideBar = ({ sidebar, setsidebar, chat, cookies }) => {
  // const { changeNickname, changeTheme, userData, addUserToChat, serviceDeleteUsers, makeAdmin } = useContext(ChatContext);
  const initialClientProfile = {
    birthDate: "",
    email: "",
    instagram: "",
    facebook: "",
    telegram: "",
    note: "",
    tags: "",
  };

  const [medias, setMedias] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [campaign, setCampaign] = useState({});
  const [photoState, setPhotoState] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [clientProfile, setClientProfile] = useState(initialClientProfile);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [blockDescription, setBlockDescription] = useState("");
  const [blockFile, setBlockFile] = useState(null);
  const [isSubmittingBlock, setIsSubmittingBlock] = useState(false);

  const canSubmitBlock = !!cookies.role.name;
  const {
    isBlocked: isNumberBlocked,
    isPending: isBlockPending,
    canRequestBlock,
    markRequestSubmitted,
  } = useContactBlockStatus(chat?.customerPhone, cookies.SIDikey);

  const resetBlockForm = () => {
    setBlockReason("");
    setBlockDescription("");
    setBlockFile(null);
  };

  const handleSubmitBlockRequest = async () => {
    if (!blockReason || !blockDescription) {
      Swal.fire("Lengkapi form", "Alasan dan deskripsi wajib diisi", "warning");
      return;
    }
    if (!blockFile) {
      Swal.fire("Lengkapi form", "Bukti pendukung wajib diupload", "warning");
      return;
    }
    if (blockFile.size > 5 * 1024 * 1024) {
      Swal.fire("File terlalu besar", "Maksimal ukuran file 5MB", "warning");
      return;
    }

    setIsSubmittingBlock(true);
    const resp = await serviceSubmitBlockRequest(
      cookies.SIDikey,
      {
        phoneNumber: chat.customerPhone,
        contactId: chat.contactId,
        chatId: chat.id,
        reason: blockReason,
        reasonDescription: blockDescription,
      },
      blockFile,
    );
    setIsSubmittingBlock(false);

    if (resp.status === 201) {
      const isInstantBlock = resp.data?.status === "APPROVED";
      markRequestSubmitted(isInstantBlock);
      Swal.fire(
        isInstantBlock ? "Diblokir" : "Terkirim",
        isInstantBlock ? "Nomor berhasil diblokir" : "Pengajuan blokir menunggu persetujuan Admin/Head",
        "success",
      );
      setIsBlockModalOpen(false);
      resetBlockForm();
    } else {
      Swal.fire("Gagal", resp?.data?.message || "Mohon coba lagi", "error");
    }
  };

  const getAllMedia = async () => {
    setIsLoading(true);
    const response = await serviceGetMedia(cookies.SIDikey, chat.id);
    if (response.status === 200) {
      setMedias(response.data.data);
    }
    // setIsLoading(false);
  };
  const refreshContact = async () => {
    const response = await serviceGetContact(cookies.SIDikey, chat.contactId);
    if (response.status === 200) {
      setContact(response.data);
    }
    // setIsLoading(false);
  };

  const refreshCampaign = async () => {
    let campaignId = null;
    if (chat.tickets) {
      campaignId = chat?.tickets[0].campaignId;
    } else {
      const ticketData = await serviceGetTicketsByUserId(cookies.SIDikey, chat.userId);
      campaignId = ticketData.data.data[0].campaignId;
    }

    if (campaignId) {
      const response = await serviceGetCampaign(cookies.SIDikey, campaignId);
      if (response.status === 200) {
        setCampaign(response.data);
      }
    } else {
      setCampaign("-");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (sidebar) {
      getAllMedia();
      refreshContact();
      refreshCampaign();
    } else {
      setMedias([]);
      setContact({});
      setCampaign({});
    }
  }, [sidebar]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClientProfileChange = (field, newValue) => {
    setClientProfile((current) => {
      return {
        ...current,
        [field]: newValue,
      };
    });
  };

  const editContact = async () => {
    setIsEditMode(false);
  };

  return (
    <SimpleBar className={`nk-chat-profile ${sidebar ? "visible" : ""}`}>
      {isLoading ? (
        <Spinner></Spinner>
      ) : (
        <React.Fragment>
          <div className="user-card pt-5 sidebar-header">
            <div className="user-card px-4">
              <UserAvatar icon="user-alt-fill"></UserAvatar>
              <div className="user-info">
                <>
                  <span className="lead-text">{utilNumberPhoneConvention(chat?.name ?? "")}</span>
                  {chat?.name !== contact?.name && <span className="sub-text ">({contact?.name})</span>}
                  <span className="sub-text ">{"+" + chat.customerPhone}</span>
                  {isNumberBlocked && (
                    <Badge color="danger" pill className="ml-1">
                      Diblokir
                    </Badge>
                  )}
                  {!isNumberBlocked && isBlockPending && (
                    <Badge color="warning" pill className="ml-1">
                      Menunggu Persetujuan
                    </Badge>
                  )}
                </>
              </div>
            </div>

            <div className="user-card-menu btn btn-icon btn-trigger text-primary chat-profile-toggle">
              <Icon name={"cross"} onClick={() => setsidebar(false)}></Icon>
            </div>
          </div>
          <Modal isOpen={isBlockModalOpen} toggle={() => setIsBlockModalOpen(false)} className="modal-dialog-centered">
            <ModalBody>
              <a
                href="#cancel"
                onClick={(ev) => {
                  ev.preventDefault();
                  setIsBlockModalOpen(false);
                }}
                className="close"
              >
                <Icon name="cross-sm"></Icon>
              </a>
              <h5 className="mb-3">Ajukan Blokir Nomor</h5>
              <p className="sub-text mb-3">{"+" + chat.customerPhone}</p>
              <FormGroup>
                <Label>Alasan Pemblokiran</Label>
                <Input type="select" value={blockReason} onChange={(e) => setBlockReason(e.target.value)}>
                  <option value="">Pilih alasan</option>
                  {BLOCK_REASON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>Deskripsi</Label>
                <Input
                  type="textarea"
                  value={blockDescription}
                  onChange={(e) => setBlockDescription(e.target.value)}
                  placeholder="Jelaskan detail masalahnya"
                />
              </FormGroup>
              <FormGroup>
                <Label>Bukti Pendukung (gambar/video, maks 5MB)</Label>
                <div className="custom-file">
                  <input
                    type="file"
                    className="custom-file-input"
                    id="blockEvidenceFileChat"
                    accept="image/*,video/*"
                    onChange={(e) => setBlockFile(e.target.files[0])}
                  />
                  <Label className="custom-file-label" htmlFor="blockEvidenceFileChat">
                    {blockFile ? blockFile.name : "Pilih file"}
                  </Label>
                </div>
              </FormGroup>
              <Button color="danger" block disabled={isSubmittingBlock} onClick={handleSubmitBlockRequest}>
                {isSubmittingBlock ? <Spinner size="sm" /> : "Kirim Pengajuan"}
              </Button>
            </ModalBody>
          </Modal>
          <div className="chat-profile" style={{ marginTop: "150px" }}>
            {canSubmitBlock && canRequestBlock && (
              <div className="px-4 pb-3">
                <Button color="danger" outline size="sm" block onClick={() => setIsBlockModalOpen(true)}>
                  Ajukan Blokir
                </Button>
              </div>
            )}
            <div className="chat-profile-group">
              <a href="#options" className={`chat-profile-head`} id="chat-options">
                <div>
                  <span className="lead-text">Birth Date</span>
                  <span className="sub-text">
                    {contact?.birthday ? moment(contact?.birthday).format("DD MMM, YYYY") : "-"}
                  </span>
                </div>
              </a>
            </div>
            <div className="chat-profile-group">
              <div className={`chat-profile-head`}>
                <span className="lead-text">Campaign</span>
                <span className="sub-text ">{campaign?.name || "-"}</span>
              </div>
            </div>
            <div className="chat-profile-group">
              <a href="#options" className={`chat-profile-head`} id="chat-options">
                <div>
                  <span className="lead-text">Emails / Social Media</span>
                  {isEditMode ? (
                    <>
                      <div className="d-flex align-items-center mb-1">
                        <Icon name="emails" className="mr-2" style={{ fontSize: 16 }}></Icon>
                        <input
                          className="form-control"
                          type="text"
                          placeholder="Email"
                          value={clientProfile.email}
                          onChange={(event) => handleClientProfileChange("email", event.target.value)}
                        />
                      </div>
                      <div className="d-flex align-items-center mb-1">
                        <Icon name="instagram" className="mr-2" style={{ fontSize: 16 }}></Icon>
                        <input
                          className="form-control"
                          type="text"
                          placeholder="Instagram"
                          value={clientProfile.instagram}
                          onChange={(event) => handleClientProfileChange("instagram", event.target.value)}
                        />
                      </div>
                      <div className="d-flex align-items-center mb-1">
                        <Icon name="facebook-f" className="mr-2" style={{ fontSize: 16 }}></Icon>
                        <input
                          className="form-control"
                          type="text"
                          placeholder="Facebook"
                          value={clientProfile.facebook}
                          onChange={(event) => handleClientProfileChange("facebook", event.target.value)}
                        />
                      </div>
                      <div className="d-flex align-items-center">
                        <Icon name="telegram" className="mr-2" style={{ fontSize: 16 }}></Icon>
                        <input
                          className="form-control"
                          type="text"
                          placeholder="Telegram"
                          value={clientProfile.telegram}
                          onChange={(event) => handleClientProfileChange("telegram", event.target.value)}
                        />
                      </div>
                    </>
                  ) : (
                    <span className="sub-text">
                      {!!contact?.emails
                        ? contact?.emails.map((email, index) => {
                            return (
                              <div key={index}>
                                - {email.email} <br></br>
                              </div>
                            );
                          })
                        : "tidak"}
                    </span>
                  )}
                </div>
              </a>
            </div>
            <div className="chat-profile-group">
              <a
                href="#options"
                // onClick={(ev) => {
                //   ev.preventDefault();
                //   setOptionState(!optionState);
                // }}
                className={`chat-profile-head`}
                id="chat-options"
              >
                <div>
                  <span className="lead-text">Note</span>
                  {isEditMode ? (
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Note"
                      value={contact?.notes}
                      onChange={(event) => handleClientProfileChange("note", event.target.value)}
                    />
                  ) : (
                    <span className="sub-text">{contact?.notes}</span>
                  )}
                </div>
              </a>
            </div>
            {!isEditMode && (
              <div className="chat-profile-group">
                <a
                  href="#options"
                  // onClick={(ev) => {
                  //   ev.preventDefault();
                  //   setOptionState(!optionState);
                  // }}
                  className={`chat-profile-head`}
                  id="chat-options"
                >
                  <div>
                    <span className="lead-text">Follow-up Date</span>
                    <span className="sub-text">
                      {chat.lastMessage?.createdAt ? moment(chat.lastMessage?.createdAt).format("DD/MM/YYYY") : ""}
                    </span>
                  </div>
                </a>
              </div>
            )}
            <div className="chat-profile-group">
              <a href="#options" className={`chat-profile-head`} id="chat-options">
                <div>
                  <span className="lead-text">Tags</span>
                  {chat.tags?.map((item, idx) => {
                    return (
                      <span
                        key={idx}
                        className="badge rounded-pill text-bg-primary"
                        style={{
                          background: item.color,
                          borderColor: "#ccc",
                          color: getTextColor(item.color),
                          margin: "2px",
                        }}
                      >
                        {item.name}
                      </span>
                    );
                  })}
                </div>
              </a>
            </div>
            <div className="chat-profile-group">
              <a
                href="#shared"
                onClick={(ev) => {
                  ev.preventDefault();
                  setPhotoState(!photoState);
                }}
                className="chat-profile-head"
              >
                <h6 className="title overline-title">Shared Docs</h6>
                <span className="indicator-icon">
                  <Icon name={`chevron-${photoState ? "up" : "down"}`}></Icon>
                </span>
              </a>
              {isLoading ? (
                <>Loading</>
              ) : (
                <div className={`chat-profile-body collapse ${photoState ? "" : "show"}`} id="chat-photos">
                  <div className="chat-profile-body-inner">
                    <ul className="chat-profile-media row">
                      {medias.map((element, index) => {
                        if (!element.content) {
                          return <div key={index}></div>;
                        }
                        if (element.type === "IMAGE") {
                          return <SharedImage link={process.env.REACT_APP_S3_URL + element.content} key={index} />;
                        } else if (element.type === "VIDEO") {
                          return <SharedVideo link={process.env.REACT_APP_S3_URL + element.content} key={index} />;
                        } else if (element.type === "TEXT") {
                        } else {
                          return (
                            <SharedDoc
                              link={process.env.REACT_APP_S3_URL + element.content}
                              extension={"." + element.type}
                              key={index}
                            />
                          );
                        }
                        return <div key={index}></div>;
                      })}
                    </ul>
                  </div>
                </div>
              )}
            </div>
            {/* .chat-profile-group */}
            {isEditMode && (
              <div className="chat-profile-head">
                <Button color="primary" block onClick={editContact}>
                  Save
                </Button>
              </div>
            )}
          </div>
        </React.Fragment>
      )}
    </SimpleBar>
  );
};
export default ChatSideBar;
